import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Power, Search, Truck } from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";
import { api, formatApiError } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { EmptyState, ErrorState, LoadingState, StatusBadge } from "../components/AsyncState";
import { Field, PageHeader } from "../components/PageHeader";
import { formatNumber } from "../lib/format";
import type { Unit } from "./UnitsPage";

type Agent = {
  id: string;
  user_id: string | null;
  user_display_name: string | null;
  username: string | null;
  agent_code: string;
  name: string;
  phone: string;
  status: "active" | "inactive";
  primary_unit_id: string;
  unit_name: string;
  units_count: number;
  unit_ids: string[];
  notes: string | null;
  version: number;
};

type AgentForm = {
  id?: string;
  userId: string;
  name: string;
  phone: string;
  primaryUnitId: string;
  unitIds: string[];
  notes: string;
  status: "active" | "inactive";
  version?: number;
};

const emptyForm: AgentForm = {
  userId: "",
  name: "",
  phone: "",
  primaryUnitId: "",
  unitIds: [],
  notes: "",
  status: "active"
};

export function AgentsPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [form, setForm] = useState<AgentForm | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ["agents", search],
    queryFn: async () =>
      (await api<Agent[]>(`/agents?limit=100${search ? `&search=${encodeURIComponent(search)}` : ""}`)).data
  });
  const units = useQuery({
    queryKey: ["units"],
    queryFn: async () => (await api<Unit[]>("/units/tree")).data,
    enabled: can("agent.manage")
  });
  const users = useQuery({
    queryKey: ["users", "agent-link-options"],
    queryFn: async () =>
      (
        await api<Array<{ id: string; display_name: string; username_normalized: string; status: string }>>(
          "/users"
        )
      ).data,
    enabled: Boolean(form) && can("user.manage")
  });
  const unitMap = useMemo(
    () => new Map((units.data ?? []).map((unit) => [unit.id, unit.name_ar])),
    [units.data]
  );

  const save = useMutation({
    mutationFn: async (value: AgentForm) => {
      const base = {
        name: value.name,
        userId: can("user.manage") ? value.userId || null : undefined,
        primaryUnitId: value.primaryUnitId,
        unitIds: [...new Set([value.primaryUnitId, ...value.unitIds])],
        notes: value.notes || undefined
      };
      if (!value.id) {
        return api("/agents", {
          method: "POST",
          body: { ...base, phone: value.phone || undefined }
        });
      }
      const updateBody: Record<string, unknown> = {
        name: value.name,
        primaryUnitId: value.primaryUnitId,
        notes: value.notes,
        status: value.status,
        expectedVersion: value.version
      };
      if (can("user.manage")) updateBody.userId = value.userId || null;
      if (value.phone.trim()) updateBody.phone = value.phone;
      const updated = await api<{ version: number }>(`/agents/${value.id}`, {
        method: "PATCH",
        body: updateBody
      });
      return api(`/agents/${value.id}/units`, {
        method: "PUT",
        body: {
          primaryUnitId: value.primaryUnitId,
          unitIds: base.unitIds,
          expectedVersion: updated.data.version
        }
      });
    },
    onSuccess: async (_, value) => {
      setNotice(value.id ? "تم حفظ بيانات الوكيل ووحداته." : "تمت إضافة الوكيل وربطه بوحداته.");
      setForm(null);
      await queryClient.invalidateQueries({ queryKey: ["agents"] });
    }
  });

  const toggle = useMutation({
    mutationFn: (agent: Agent) =>
      api(`/agents/${agent.id}`, {
        method: "PATCH",
        body: {
          status: agent.status === "active" ? "inactive" : "active",
          expectedVersion: agent.version
        }
      }),
    onSuccess: async () => {
      setNotice("تم تحديث حالة الوكيل مع الاحتفاظ بتاريخ دفعاته وتسليماته.");
      await queryClient.invalidateQueries({ queryKey: ["agents"] });
    }
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (form) save.mutate(form);
  }

  function toggleUnit(unitId: string, checked: boolean) {
    if (!form) return;
    const unitIds = checked
      ? [...new Set([...form.unitIds, unitId])]
      : form.unitIds.filter((id) => id !== unitId);
    setForm({ ...form, unitIds });
  }

  return (
    <div className="page">
      <PageHeader
        title="إدارة الوكلاء"
        description="إضافة الوكلاء وتعديلهم وربط كل وكيل بوحدة أساسية وعدة نطاقات خدمة."
        actions={
          can("agent.manage") ? (
            <button className="button" type="button" onClick={() => setForm(emptyForm)}>
              <Plus aria-hidden="true" /> إضافة وكيل
            </button>
          ) : undefined
        }
      />
      {notice ? (
        <div className="success-panel" role="status">
          <Truck aria-hidden="true" /> <p>{notice}</p>
        </div>
      ) : null}
      {form ? (
        <section className="content-card management-form" aria-labelledby="agent-form-title">
          <div className="section-head">
            <div>
              <h2 id="agent-form-title">{form.id ? "تعديل الوكيل" : "إضافة وكيل"}</h2>
              <p>يُحفظ الهاتف مشفرًا، ولا يظهر بعد الحفظ إلا بصورة محجوبة.</p>
            </div>
          </div>
          <form className="form-grid" onSubmit={submit}>
            <Field label="اسم الوكيل" htmlFor="agent-name">
              <input
                id="agent-name"
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                required
              />
            </Field>
            <Field
              label="رقم الهاتف"
              htmlFor="agent-phone"
              hint={form.id ? "اتركه فارغًا للإبقاء على الرقم الحالي." : "اختياري"}
            >
              <input
                id="agent-phone"
                dir="ltr"
                inputMode="tel"
                value={form.phone}
                onChange={(event) => setForm({ ...form, phone: event.target.value })}
              />
            </Field>
            {can("user.manage") ? (
              <Field
                label="حساب دخول الوكيل"
                htmlFor="agent-user"
                hint="اختر حسابًا يحمل دور موظف التسليم. لا يمكن ربط الحساب بأكثر من وكيل."
              >
                <select
                  id="agent-user"
                  value={form.userId}
                  onChange={(event) => setForm({ ...form, userId: event.target.value })}
                >
                  <option value="">بدون حساب مرتبط</option>
                  {(users.data ?? [])
                    .filter((user) => user.status === "active")
                    .map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.display_name} ({user.username_normalized})
                      </option>
                    ))}
                </select>
              </Field>
            ) : null}
            <Field label="الوحدة الأساسية" htmlFor="agent-primary">
              <select
                id="agent-primary"
                value={form.primaryUnitId}
                onChange={(event) => {
                  const id = event.target.value;
                  setForm({
                    ...form,
                    primaryUnitId: id,
                    unitIds: id ? [...new Set([...form.unitIds, id])] : form.unitIds
                  });
                }}
                required
              >
                <option value="">اختر الوحدة</option>
                {(units.data ?? [])
                  .filter((unit) => unit.status === "active")
                  .map((unit) => (
                    <option key={unit.id} value={unit.id}>
                      {unit.name_ar}
                    </option>
                  ))}
              </select>
            </Field>
            {form.id ? (
              <Field label="الحالة" htmlFor="agent-status">
                <select
                  id="agent-status"
                  value={form.status}
                  onChange={(event) =>
                    setForm({ ...form, status: event.target.value as AgentForm["status"] })
                  }
                >
                  <option value="active">نشط</option>
                  <option value="inactive">معطل</option>
                </select>
              </Field>
            ) : null}
            <Field label="ملاحظات" htmlFor="agent-notes">
              <textarea
                id="agent-notes"
                rows={3}
                value={form.notes}
                onChange={(event) => setForm({ ...form, notes: event.target.value })}
              />
            </Field>
            <fieldset className="form-span checkbox-fieldset">
              <legend>الوحدات التي يخدمها الوكيل</legend>
              {units.isPending ? (
                <p>جارٍ تحميل الوحدات…</p>
              ) : units.isError ? (
                <p className="field-error">{formatApiError(units.error)}</p>
              ) : (
                <div className="checkbox-grid">
                  {units.data
                    .filter((unit) => unit.status === "active")
                    .map((unit) => (
                      <label className="check-option" key={unit.id}>
                        <input
                          type="checkbox"
                          checked={form.unitIds.includes(unit.id) || form.primaryUnitId === unit.id}
                          disabled={form.primaryUnitId === unit.id}
                          onChange={(event) => toggleUnit(unit.id, event.target.checked)}
                        />
                        <span>{unit.name_ar}</span>
                      </label>
                    ))}
                </div>
              )}
            </fieldset>
            {save.isError ? (
              <div className="inline-alert form-span" role="alert">
                {formatApiError(save.error)}
              </div>
            ) : null}
            <div className="dialog-actions form-span">
              <button className="button button--secondary" type="button" onClick={() => setForm(null)}>
                إلغاء
              </button>
              <button className="button" type="submit" disabled={save.isPending || !form.primaryUnitId}>
                {save.isPending ? "جارٍ الحفظ…" : "حفظ الوكيل"}
              </button>
            </div>
          </form>
        </section>
      ) : null}
      <section className="content-card filters-card">
        <label className="search-field">
          <span className="sr-only">بحث الوكلاء</span>
          <Search aria-hidden="true" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="اسم الوكيل أو رمزه"
          />
        </label>
      </section>
      <section className="content-card">
        {query.isPending ? (
          <LoadingState />
        ) : query.isError ? (
          <ErrorState error={query.error} retry={() => void query.refetch()} />
        ) : query.data.length === 0 ? (
          <EmptyState title="لا يوجد وكلاء" description="أضف وكيلًا واربطه بوحدة سكنية للبدء." />
        ) : (
          <div className="card-list">
            {query.data.map((agent) => (
              <article className="agent-card" key={agent.id}>
                <div className="avatar">
                  <Truck aria-hidden="true" />
                </div>
                <div className="grow">
                  <div className="row-between">
                    <div>
                      <h2>{agent.name}</h2>
                      <p>{agent.agent_code}</p>
                    </div>
                    <StatusBadge status={agent.status} />
                  </div>
                  <dl className="inline-details">
                    <div>
                      <dt>الوحدة الأساسية</dt>
                      <dd>{agent.unit_name}</dd>
                    </div>
                    <div>
                      <dt>الوحدات المخدومة</dt>
                      <dd>{formatNumber(agent.units_count)}</dd>
                    </div>
                    <div>
                      <dt>الهاتف</dt>
                      <dd dir="ltr">{agent.phone}</dd>
                    </div>
                    <div>
                      <dt>حساب الدخول</dt>
                      <dd>
                        {agent.user_display_name
                          ? `${agent.user_display_name} (${agent.username})`
                          : "غير مرتبط"}
                      </dd>
                    </div>
                  </dl>
                  {agent.unit_ids.length > 1 ? (
                    <p className="table-subline">
                      النطاقات:{" "}
                      {agent.unit_ids
                        .map((id) => unitMap.get(id))
                        .filter(Boolean)
                        .join("، ") || "عدة وحدات"}
                    </p>
                  ) : null}
                  {can("agent.manage") ? (
                    <div className="action-row">
                      <button
                        className="button button--ghost"
                        type="button"
                        onClick={() => setForm(toForm(agent))}
                      >
                        <Pencil aria-hidden="true" /> تعديل
                      </button>
                      <button
                        className="button button--ghost"
                        type="button"
                        disabled={toggle.isPending}
                        onClick={() => toggle.mutate(agent)}
                      >
                        <Power aria-hidden="true" /> {agent.status === "active" ? "تعطيل" : "تنشيط"}
                      </button>
                    </div>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
        )}
        {toggle.isError ? (
          <div className="inline-alert" role="alert">
            {formatApiError(toggle.error)}
          </div>
        ) : null}
      </section>
    </div>
  );
}

function toForm(agent: Agent): AgentForm {
  return {
    id: agent.id,
    userId: agent.user_id ?? "",
    name: agent.name,
    phone: "",
    primaryUnitId: agent.primary_unit_id,
    unitIds: agent.unit_ids,
    notes: agent.notes ?? "",
    status: agent.status,
    version: agent.version
  };
}
